import { hasAnyPermission, useAuthorization } from '@frontend/authorization';
import { EntityType, Rights, useAppSelector, useLocalStorageState } from '@frontend/common';
import { Application, Feature, FunctionalityType } from '@frontend/functionality/types';
import { useLicenseRepository } from '@frontend/repository';
import { UserClient } from '@frontend/user/api';
import { fetchProfile, updateCurrent, userStore } from '@frontend/user/redux';
import { IUser } from '@frontend/user/types';
import { useCacheBusting } from '@frontend/version/hooks';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';

import { VERSION } from '../../version';
import { useAppDispatch } from '../redux/store';
import { Props } from './header-component';

interface ViewProps {
    navbarPinned: boolean;
    user?: IUser;
    changeNavbarPinned: (value: boolean) => void;
    navigate: NavigateFunction;
    updateAvailable: boolean;
    updateApplication: () => void;
    globalSearch: boolean;
    changeGlobalSearch: (value: boolean) => void;
    showLanguageSelect: boolean;
    setShowLanguageSelect: (value: boolean) => void;
    adminEnabled: boolean;
    showBreadcrumbs: boolean;
}

const useHeader = (props: Props): ViewProps => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { functionalities } = useLicenseRepository();
    const { permissions } = useAuthorization();
    const { updateAvailable, update } = useCacheBusting({ currentVersion: VERSION, repository: 'platform' });
    const [user, setUser] = useState<IUser | undefined>(undefined);
    const [navbarPinned, changeNavbarPinned] = useLocalStorageState<boolean>('navbar-pinned', true);
    const [globalSearch, changeGlobalSearch] = useState<boolean>(false);
    const [showLanguageSelect, setShowLanguageSelect] = useState<boolean>(false);
    const [adminEnabled, setAdminEnabled] = useState<boolean>(false);
    const navigate = useNavigate();
    const userState = useAppSelector(useSelector, userStore);

    useEffect(() => {
        dispatch(fetchProfile());
    }, []);

    useEffect(() => {
        if (permissions && hasAnyPermission(permissions, [{ item: EntityType.FUNCTIONALITY, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' }])) {
            setAdminEnabled(true);
            return;
        }
        const functionality = functionalities.find(
            (func) => func.application === Application.ADMIN && func.type === FunctionalityType.CORPORATE && func.feature === Feature.ADMIN
        );
        if (functionality) {
            setAdminEnabled(true);
            return;
        }
        setAdminEnabled(false);
    }, [functionalities, permissions]);

    useEffect(() => {
        if (userState.current) setUser(userState.current);
        else {
            UserClient.fetchMyself().then((fetchedUser) => {
                setUser(fetchedUser);
                dispatch(updateCurrent(fetchedUser));
            });
        }
    }, [userState.current]);

    return {
        navbarPinned,
        user,
        changeNavbarPinned,
        navigate,
        updateAvailable,
        updateApplication: update,
        globalSearch,
        changeGlobalSearch,
        showLanguageSelect,
        setShowLanguageSelect,
        adminEnabled,
        showBreadcrumbs: location.pathname.includes('/admin')
    };
};

export default useHeader;
